import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { IonicModule, ModalController } from '@ionic/angular';
import { firstValueFrom } from 'rxjs';

/**
 *  ZÁRADÉK-SABLONOK ÉS A 20/2012. EMMI R. 1. MELLÉKLETE – LEFEDETTSÉG (GET /api/zaradek/melleklet/lefedettseg)
 *
 *  A rendelet 112. § (4): „Az iskola által alkalmazható záradékokat az 1. melléklet tartalmazza.” A
 *  backend a melléklet II. részének kanonikus listáját (backend/zaradek-melleklet.json, njt.hu) veti össze
 *  a záradék-sablonokkal (zaradek_sablon.melleklet_kod): melyik mellékleti záradékhoz nincs sablon, melyik
 *  sablon hivatkozik a mellékletben nem létező kódra, és hol tér el a sablon szövege a mellékletitől.
 *  A záradék-ablakból (záradék-modál) nyílik, munkatársnak.
 */
interface Tetel {
  kod: string; tipus: 'iskola' | 'kollegium'; szoveg: string; dokumentumok: string[]; dokumentumok_jelolese: string | null;
  sablon_kell: boolean; megjegyzes: string | null; allapot: 'van' | 'hianyzik' | 'inaktiv' | 'nem_kell';
  sablonok: Array<{ id: string; aktiv: boolean; kategoria: string; szoveg_elter: boolean }>;
}
interface Lefedettseg {
  jogszabaly: string; melleklet: string; forras: string; lekerve: string;
  kihagyott: Array<{ sorszam: number; ok: string }>;
  osszesites: { mellekleti: number; van: number; hianyzik: number; inaktiv: number; nem_kell: number; ismeretlen_kod: number; szoveg_elter: number; kod_nelkuli_sablon: number };
  tetelek: Tetel[];
  ismeretlen_kod: Array<{ id: string; melleklet_kod: string; aktiv: boolean; kategoria: string; szoveg: string }>;
}

@Component({
  selector: 'app-zaradek-lefedettseg',
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  templateUrl: './zaradek-lefedettseg.component.html',
})
export class ZaradekLefedettsegComponent implements OnInit {
  @Input() intezmenyId = '';

  adat: Lefedettseg | null = null;
  hiba = '';
  betolt = false;
  szuro: 'gond' | 'mind' = 'gond';

  constructor(private http: HttpClient, private modalCtrl: ModalController) {}

  ngOnInit() { this.betoltes(); }

  async betoltes() {
    this.betolt = true;
    this.hiba = '';
    try {
      const q = this.intezmenyId ? `?intezmeny=${encodeURIComponent(this.intezmenyId)}` : '';
      this.adat = await firstValueFrom(this.http.get<Lefedettseg>(`/api/zaradek/melleklet/lefedettseg${q}`));
    } catch (e: any) {
      this.adat = null;
      this.hiba = e?.error?.error || 'Nem sikerült betölteni a lefedettséget.';
    } finally {
      this.betolt = false;
    }
  }

  /** Gond: nincs aktív sablon, csak inaktív van, vagy az aktív sablon szövege eltér. */
  gondE(t: Tetel): boolean {
    return t.allapot === 'hianyzik' || t.allapot === 'inaktiv' || t.sablonok.some((s) => s.aktiv && s.szoveg_elter);
  }

  get lathatoTetelek(): Tetel[] {
    const l = this.adat?.tetelek || [];
    return this.szuro === 'gond' ? l.filter((t) => this.gondE(t)) : l;
  }

  allapotSzoveg(t: Tetel): string {
    switch (t.allapot) {
      case 'van': return t.sablonok.some((s) => s.aktiv && s.szoveg_elter) ? 'Van sablon (eltérő szöveg)' : 'Van sablon';
      case 'inaktiv': return 'Csak inaktív sablon';
      case 'hianyzik': return 'Nincs sablon';
      case 'nem_kell': return 'Sablon nem kell';
      default: return '';
    }
  }

  allapotSzin(t: Tetel): string {
    if (t.allapot === 'hianyzik') return 'danger';
    if (t.allapot === 'inaktiv' || t.sablonok.some((s) => s.aktiv && s.szoveg_elter)) return 'warning';
    if (t.allapot === 'nem_kell') return 'medium';
    return 'success';
  }

  bezar() { this.modalCtrl.dismiss(); }
}
